import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Drawer,
  Group,
  ScrollArea,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { prospectInputSchema, type ProspectInputSchema } from '../../lib/schemas';
import { useCreateProspect, useUpdateProspect } from '../../queries/prospects';
import { ApiError } from '../../lib/api-error';
import type { Prospect } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  prospect?: Prospect | null;
  onSaved?: (p: Prospect) => void;
}

const EMPTY: ProspectInputSchema = {
  name: '',
  description: null,
};

export function ProspectFormDrawer({ opened, onClose, prospect, onSaved }: Props) {
  const isEdit = !!prospect;
  const createM = useCreateProspect();
  const updateM = useUpdateProspect();
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<ProspectInputSchema>({
    resolver: zodResolver(prospectInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${prospect?.id ?? 'new'}-${prospect?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) {
      form.reset(
        prospect
          ? { name: prospect.name, description: prospect.description }
          : EMPTY,
      );
    }
  }
  useEffect(() => undefined, [form]);

  async function onSubmit(values: ProspectInputSchema) {
    try {
      if (isEdit && prospect) {
        const res = await updateM.mutateAsync({ id: prospect.id, patch: values });
        notifications.show({ title: 'Prospect saved', message: res.prospect.name, color: 'pine' });
        onSaved?.(res.prospect);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({ title: 'Prospect created', message: res.prospect.name, color: 'pine' });
        onSaved?.(res.prospect);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit prospect' : 'New prospect'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. North Wolfberry Block"
            required
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <Textarea
                label="Description"
                autosize
                minRows={3}
                placeholder="Optional context, target formation, AOI bounds, etc."
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.currentTarget.value || null)}
              />
            )}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create prospect'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
